import {
  useCallback,
  type RefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useValidationAndFormatting } from './useValidationAndFormatting.js';
import { constrainValue } from '../../helper.js';
import { useSelection } from '../../hooks/useSelection.js';
import { NUMBER_SPIN_BUTTON_DEFAULTS } from '../constants.js';
import { NumberSpinButtonProps } from '../types.js';

/**
 * This hook provides a keydown handler to navigate a given value range.
 */
export const useValueNavigation = (
  ref: RefObject<HTMLElement | null> | undefined,
  props: NumberSpinButtonProps,
  applyText?: (value: string, fireOnChange?: boolean) => void,
) => {
  const {
    disabled,
    readOnly,
    onChange,
    displayFormat,
    required,
    minValue,
    maxValue,
    value,
    isCyclic,
  } = props;
  const { selectAll } = useSelection(ref);
  const { format } = useValidationAndFormatting(
    displayFormat,
    required,
    minValue,
    maxValue,
  );

  // Default transformer functions for spinbutton keyboard handlers
  const {
    onArrowUp = (value, e) => {
      e.preventDefault();
      return (value ?? 0) + NUMBER_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onArrowDown = (value, e) => {
      e.preventDefault();
      return (value ?? 0) - NUMBER_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onPageUp = (value, e) => {
      e.preventDefault();
      return (value ?? 0) + NUMBER_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onPageDown = (value, e) => {
      e.preventDefault();
      return (value ?? 0) - NUMBER_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onHome = (value, e) => {
      // Only execute if a minValue prop was defined
      if (minValue !== undefined) {
        e.preventDefault();
        return minValue;
      }
      return value;
    },
    onEnd = (value, e) => {
      // Only execute if a maxValue prop was defined
      if (maxValue !== undefined) {
        e.preventDefault();
        return maxValue;
      }
      return value;
    },
  } = props;

  const onKeyDownHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (disabled || readOnly) {
        // Prevent any value changes via keys, but don't prevent the default behavior.
        return;
      }

      if (e.shiftKey || e.key === 'Shift') {
        return;
      }

      // Treat NaN the same as null so that ArrowUp/ArrowDown start cycling
      // from the min/max boundary instead of producing NaN arithmetic.
      const safeValue =
        value !== null && value !== undefined && !Number.isNaN(value)
          ? value
          : null;

      // Ensures, the given value is always sanitized before updating the inner state.
      const updateValue = (newValue: number | null) => {
        const sanitizedValue = constrainValue(
          newValue,
          minValue,
          maxValue,
          isCyclic,
        );
        if (safeValue === sanitizedValue) {
          return;
        }

        const newText = format(sanitizedValue);
        applyText?.(newText);
        onChange?.(sanitizedValue);
        selectAll();
      };

      switch (e.key) {
        case 'ArrowUp':
          updateValue(onArrowUp(safeValue ?? 0, e));
          break;
        case 'ArrowDown':
          updateValue(onArrowDown(safeValue ?? 0, e));
          break;
        case 'PageUp':
          updateValue(onPageUp(safeValue ?? 0, e));
          break;
        case 'PageDown':
          updateValue(onPageDown(safeValue ?? 0, e));
          break;
        case 'Home':
          updateValue(onHome(safeValue ?? 0, e));
          break;
        case 'End':
          updateValue(onEnd(safeValue ?? 0, e));
          break;
      }
    },
    [
      disabled,
      readOnly,
      minValue,
      maxValue,
      isCyclic,
      value,
      format,
      applyText,
      onChange,
      selectAll,
      onArrowUp,
      onArrowDown,
      onPageUp,
      onPageDown,
      onHome,
      onEnd,
    ],
  );
  return { onKeyDownHandler };
};
